// CustomWordCloud.js
import React, { useEffect, useRef, useState, useMemo } from "react";
import Cloud from "react-d3-cloud";
import styled from "styled-components";

const FallbackMessage = styled.div`
  width: 100%;
  height: 270px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #6b6b6b;
  font-size: 14px;
  font-family: inherit;
`;

const CustomWordCloud = ({
  isHomePage = false,
  words = [],
  cloudHeight = "55vh",
  minFontSize = 14,
  maxFontSize = 60,
  wordCloudTypeInsta = "",
}) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: 300,
    height: 300,
  });

  // ✅ Responsive Resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({
        width: width,
        height: height,
      });
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  // ✅ Sort words (important for center dominance like Figma)
  const sortedWords = useMemo(() => {
    return [...words].sort((a, b) => b.value - a.value);
  }, [words]);

  // ✅ Min/Max values
  const minWordValue = Math.min(...sortedWords.map((w) => w.value || 0));
  const maxWordValue = Math.max(...sortedWords.map((w) => w.value || 0));

  // ✅ Font Size Mapping (smooth curve)
  const fontSizeMapper = (word) => {
    if (!word?.value) return minFontSize;

    if (maxWordValue === minWordValue) {
      return (minFontSize + maxFontSize) / 2;
    }

    const scale =
      (word.value - minWordValue) / (maxWordValue - minWordValue);

    const size =
      minFontSize +
      Math.pow(scale, 0.5) * (maxFontSize - minFontSize);

    if (wordCloudTypeInsta === "Emoji's") {
      return Math.round(size + 4);
    }

    return Math.round(size);
  };

  // ✅ Color Mapping
  const fill = (word) => {
    switch (word.color) {
      case "positive":
        return "rgba(3, 198, 82, 1)";
      case "negative":
        return "rgba(243, 27, 27, 1)";
      case "neutral":
        return "rgba(239, 205, 29, 1)";
      case "joy":
        return "rgba(218, 6, 184, 1)";
      case "surprise":
        return "rgba(3, 198, 82, 1)";
      case "neutralEmotion":
        return "rgba(239, 205, 29, 1)";
      case "anger":
        return "rgba(238, 19, 7, 1)";
      case "disgusted":
        return "rgba(84, 30, 192, 1)";
      case "sadness":
        return "rgba(21, 112, 203, 1)";
      case "fear":
        return "rgba(255, 153, 0, 1)";
      default:
        return "#888";
    }
  };

  // ✅ Slight rotation (Figma-like feel)
  const rotate = () => (Math.random() > 0.85 ? 90 : 0);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: cloudHeight,
        minHeight: "300px",
        display: sortedWords.length > 0 ? "block" : "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {sortedWords.length > 0 ? (
        <Cloud
          data={sortedWords}
          width={dimensions.width}
          height={dimensions.height}
          // padding={2}
          padding={wordCloudTypeInsta === "Emoji's" ? 1 : 2}
          spiral="archimedean"
          rotate={rotate}
          font="Inter, sans-serif"
          fontSize={fontSizeMapper}
          fill={fill}
          fontWeight="600"
        />
      ) : isHomePage ? (
        <FallbackMessage>Insufficient Data</FallbackMessage>
      ) : (
        "Insufficient Data"
      )}
    </div>
  );
};

export default CustomWordCloud;